/**
 * Explorer Timeline Service
 *
 * Manages historical timeline events for Explorer → Explore Connections
 * Each song can have up to ~10 curated timeline moments
 */

import { supabase } from './supabaseClient';

export interface TimelineEvent {
  id: string;
  sort_order: number;
  year: string;
  title: string;
  description: string;
  image_path: string | null;
  detail_image_path: string | null;
}

export interface TimelineData {
  song_id: string;
  events: TimelineEvent[];
}

/**
 * Fetch timeline events for a song
 * Returns events ordered by sort_order, then year
 */
export async function fetchTimelineEvents(songId: string): Promise<TimelineEvent[]> {
  try {
    const { data, error } = await supabase
      .from('song_explorer_timeline_events')
      .select('*')
      .eq('song_id', songId)
      .order('sort_order', { ascending: true })
      .order('year', { ascending: true });

    if (error) {
      console.error('[fetchTimelineEvents] Error:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('[fetchTimelineEvents] Unexpected error:', err);
    return [];
  }
}

/**
 * Admin: Upsert a timeline event
 */
export async function upsertTimelineEvent(params: {
  id?: string;
  song_id: string;
  sort_order: number;
  year: string;
  title: string;
  description: string;
  image_path: string | null;
  detail_image_path: string | null;
}): Promise<{ success: boolean; data?: TimelineEvent; error?: string }> {
  try {
    const payload = {
      song_id: params.song_id,
      sort_order: params.sort_order,
      year: params.year,
      title: params.title,
      description: params.description,
      image_path: params.image_path,
      detail_image_path: params.detail_image_path,
    };

    if (params.id) {
      // Update existing
      const { data, error } = await supabase
        .from('song_explorer_timeline_events')
        .update(payload)
        .eq('id', params.id)
        .select()
        .single();

      if (error) {
        console.error('[upsertTimelineEvent] Update error:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } else {
      // Insert new
      const { data, error } = await supabase
        .from('song_explorer_timeline_events')
        .insert([payload])
        .select()
        .single();

      if (error) {
        console.error('[upsertTimelineEvent] Insert error:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    }
  } catch (err) {
    console.error('[upsertTimelineEvent] Unexpected error:', err);
    return { success: false, error: String(err) };
  }
}

/**
 * Admin: Delete a timeline event
 */
export async function deleteTimelineEvent(
  eventId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('song_explorer_timeline_events')
      .delete()
      .eq('id', eventId);

    if (error) {
      console.error('[deleteTimelineEvent] Error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('[deleteTimelineEvent] Unexpected error:', err);
    return { success: false, error: String(err) };
  }
}

/**
 * Admin: Bulk save all timeline events for a song
 * Deletes removed events, upserts existing/new ones
 */
export async function saveAllTimelineEvents(
  songId: string,
  events: Array<{
    id?: string;
    sort_order: number;
    year: string;
    title: string;
    description: string;
    image_path: string | null;
    detail_image_path: string | null;
  }>
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get existing event IDs
    const existing = await fetchTimelineEvents(songId);
    const existingIds = existing.map((e) => e.id);
    const newIds = events.filter((e) => e.id).map((e) => e.id!);

    // Delete removed events
    const toDelete = existingIds.filter((id) => !newIds.includes(id));
    for (const id of toDelete) {
      await deleteTimelineEvent(id);
    }

    // Upsert all events
    for (const event of events) {
      await upsertTimelineEvent({
        id: event.id,
        song_id: songId,
        sort_order: event.sort_order,
        year: event.year,
        title: event.title,
        description: event.description,
        image_path: event.image_path,
        detail_image_path: event.detail_image_path,
      });
    }

    return { success: true };
  } catch (err) {
    console.error('[saveAllTimelineEvents] Error:', err);
    return { success: false, error: String(err) };
  }
}

/**
 * Get public URL for timeline image
 */
export function getTimelineImageUrl(imagePath: string | null): string {
  if (!imagePath) return '';

  const { data } = supabase.storage.from('timeline-images').getPublicUrl(imagePath);

  return data?.publicUrl || '';
}

/**
 * Admin: Upload timeline image to Supabase Storage
 */
export async function uploadTimelineImage(
  file: File,
  songId: string
): Promise<{ success: boolean; path?: string; error?: string }> {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${songId}-${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('timeline-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      console.error('[uploadTimelineImage] Upload error:', uploadError);
      return { success: false, error: uploadError.message };
    }

    return { success: true, path: filePath };
  } catch (err) {
    console.error('[uploadTimelineImage] Unexpected error:', err);
    return { success: false, error: String(err) };
  }
}
